import { motion, useReducedMotion } from 'framer-motion'
import { FadeInSection } from '../../../components/ui'
import { USE_CASES } from '../../../data'

const USE_CASE_COLORS = ['#FBE6D4', '#FFE8C2', '#E7F0E3', '#F4E1F0', '#DCE7F5', '#FFD9D9']

const USE_CASE_ICONS: Record<string, React.ReactNode> = {
  'Salon & Spa': (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 21a7 7 0 0 1 14 0M12 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  'Café & Bakery': (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 11h12v6a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-6zM16 13h2a3 3 0 1 1 0 6h-2M8 3l-.5 2M12 3l-.5 2M16 3l-.5 2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  'Yoga Studio': (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="5" r="2.2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M5 21c1-4 4-7 7-7s6 3 7 7M9 11h6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  'Pet Services': (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 13a3.5 3.5 0 0 0-3.5 3.5c0 1.4 1 2.5 2.5 2.5h2c1.5 0 2.5-1.1 2.5-2.5A3.5 3.5 0 0 0 12 13z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="6" cy="9" r="1.6" stroke="currentColor" strokeWidth="2" />
      <circle cx="18" cy="9" r="1.6" stroke="currentColor" strokeWidth="2" />
      <circle cx="9" cy="5" r="1.6" stroke="currentColor" strokeWidth="2" />
      <circle cx="15" cy="5" r="1.6" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  Freelancers: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M8 21h8M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  'Local Services': (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14.7 6.3a3 3 0 0 0-4.2 0L4.5 12.3a2 2 0 0 0 0 2.8l4.4 4.4a2 2 0 0 0 2.8 0l6-6a3 3 0 0 0 0-4.2l-3-3z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="8.5" cy="15.5" r="1.2" fill="currentColor" />
    </svg>
  ),
}

export default function WhoItsForSection() {
  const shouldReduceMotion = useReducedMotion()

  return (
    <section
      id="who-its-for"
      className="py-28 px-6"
      style={{
        backgroundColor: '#fff',
        borderBottom: '2px solid #111',
      }}
    >
      <div className="max-w-6xl mx-auto">
        <FadeInSection>
          <div className="max-w-2xl mb-16">
            <span
              className="inline-block text-xs font-bold uppercase font-dm-mono"
              style={{
                backgroundColor: '#111',
                color: '#fff',
                padding: '5px 12px',
                borderRadius: 999,
                letterSpacing: '0.14em',
              }}
            >
              Built for
            </span>
            <h2
              className="font-extrabold mt-5 tracking-tight"
              style={{
                color: '#111',
                fontSize: 'clamp(2.25rem, 5vw, 3.75rem)',
                lineHeight: 1,
                letterSpacing: '-0.03em',
              }}
            >
              If customers can
              <br />
              become regulars,
              <br />
              <span style={{ color: 'var(--accent)' }}>Nudge helps.</span>
            </h2>
            <p
              className="mt-5 text-lg"
              style={{ color: '#3a2e26', lineHeight: 1.55, maxWidth: 560 }}
            >
              Whether you run a solo operation or a growing local business — if customers
              can come back, Nudge helps them do it.
            </p>
          </div>
        </FadeInSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {USE_CASES.map((item, i) => (
            <FadeInSection key={item.title} delay={i * 0.06}>
              <motion.div
                whileHover={shouldReduceMotion ? {} : { translateX: -2, translateY: -2 }}
                transition={{ type: 'spring', stiffness: 320, damping: 22 }}
                className="who-tile flex flex-col h-full cursor-default"
                style={{
                  backgroundColor: USE_CASE_COLORS[i % USE_CASE_COLORS.length],
                  border: '2px solid #111',
                  borderRadius: 18,
                  boxShadow: '6px 6px 0 #111',
                  padding: '28px',
                }}
              >
                <div
                  className="flex items-center justify-center mb-5"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    backgroundColor: '#111',
                    color: '#fff',
                  }}
                >
                  {USE_CASE_ICONS[item.title] ?? null}
                </div>
                <h3
                  className="font-extrabold mb-2 tracking-tight"
                  style={{ color: '#111', fontSize: '1.35rem', lineHeight: 1.2, letterSpacing: '-0.015em' }}
                >
                  {item.title}
                </h3>
                <p
                  className="flex-1 text-[15px]"
                  style={{ color: '#3a2e26', lineHeight: 1.55 }}
                >
                  {item.desc}
                </p>
                <div
                  className="mt-6 pt-4 flex items-center gap-2"
                  style={{ borderTop: '1.5px solid rgba(17,17,17,0.15)' }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'var(--accent)',
                    }}
                  />
                  <span
                    className="text-xs font-bold font-dm-mono"
                    style={{ color: '#111', letterSpacing: '0.04em' }}
                  >
                    {item.stat}
                  </span>
                </div>
              </motion.div>
            </FadeInSection>
          ))}
        </div>

        <style>{`
          .who-tile { transition: transform 150ms ease, box-shadow 150ms ease; }
          .who-tile:hover { box-shadow: 8px 8px 0 #111 !important; }
        `}</style>
      </div>
    </section>
  )
}
